import React, {useMemo, useState, useEffect} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {INotification} from '../../redux/notifications/interfaces';
import {toInternalPath} from '../../utils/notifications/notificationLink';

interface NotificationItemProps {
  notification: INotification;
  onMarkRead: (notification: INotification) => void;
}

const formatScheduledTime = (
  time: string | Date | null | undefined,
  locale: string
): string => {
  if (!time) return '';

  const date = time instanceof Date ? time : new Date(time);

  if (Number.isNaN(date.getTime())) {
    return String(time);
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkRead
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const formattedDate = useMemo(
    () => formatScheduledTime(notification.date, intl.locale),
    [notification.date, intl.locale]
  );

  const markReadIfNeeded = () => {
    if (!notification.indRead) {
      onMarkRead(notification);
    }
  };

  const handleOpen = () => {
    // UCSF-3791: a notification with a target opens it; one without keeps the popup.
    const link = toInternalPath(notification.link);

    if (link) {
      markReadIfNeeded();
      navigate(link);

      return;
    }
    setIsPopupOpen(true);
  };

  const handleClose = () => {
    setIsPopupOpen(false);
    markReadIfNeeded();
  };

  const handleMarkRead = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onMarkRead(notification);
  };

  useEffect(() => {
    if (!isPopupOpen) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isPopupOpen, handleClose]);

  return (
    <>
      <li className={`notifications__item${!notification.indRead ? ' notifications__item--unread' : ''}`}>
        <button
          type="button"
          className="notifications__item-inner"
          onClick={handleOpen}>
          <div className="notifications__item-inner">
            <div className="notifications__item-header">
              <span className="notifications__item-subject">{notification.subject}</span>

              <span className="notifications__item-date">{formattedDate}</span>

              {!notification.indRead && (
                <button
                  type="button"
                  className="notifications__mark-read-btn"
                  onClick={handleMarkRead}
                >
                  <FormattedMessage id="gritx.notifications.markAsRead" defaultMessage="Mark as read"/>
                </button>
              )}
            </div>

            <div
              className="notifications__item-body"
              dangerouslySetInnerHTML={{__html: notification.body || ''}}
            />
          </div>
        </button>
      </li>

      {isPopupOpen && (
        <div className="notifications__popup-overlay">
          <button
            type="button"
            className="notifications__popup-backdrop"
            onClick={handleClose}
            aria-label="Close notification popup"
          />

          <div
            className="notifications__popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`notification-${notification.id}-title`}
          >
            <div className="notifications__popup-header">
              <div className="notifications__popup-header-wrapper">
                <h2
                  id={`notification-${notification.id}-title`}
                  className="notifications__popup-subject"
                >
                  {notification.subject}
                </h2>

                <span className="notifications__popup-date">{formattedDate}</span>
              </div>

              <button
                type="button"
                className="notifications__popup-close"
                onClick={handleClose}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div
              className="notifications__popup-body"
              dangerouslySetInnerHTML={{__html: notification.body || ''}}
            />
          </div>
        </div>
      )}
    </>
  );
};
